import { useEffect, useRef } from 'react';

type Props = {
  isLoading?: boolean;
  progress: number;
  maxValue: number;
  color: string;
  size?: number;
  thickness?: number;
  backgroundColor?: string;
};

const CanvasHalfCircle = ({
  progress,
  maxValue,
  color,
  isLoading,
  size = 218,
  thickness = 15,
  backgroundColor = '#3BB0FF33',
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  let percentage = isLoading ? 0 : ((progress > 0 ? progress : 0) / (maxValue > 0 ? maxValue : 1)) * 100;
  if (progress <= 0 || maxValue <= 0) {
    percentage = 0;
  }
  if (progress >= maxValue) {
    percentage = 100;
  }
  const additionalWidthOverHalfCircle = 0.04;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = (size * dpr);
    canvas.height = (size * dpr) / 1.6;
    canvas.style.width = `${size / 1.05}px`;
    canvas.style.height = `${size / 1.6}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const center = size / 2;
    const radius = center - thickness / 1.5;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Background ring
    ctx.beginPath();
    ctx.lineWidth = thickness;
    ctx.strokeStyle = backgroundColor;
    ctx.lineCap = 'round';
    // eslint-disable-next-line max-len
    ctx.arc(center, center, radius, Math.PI - (Math.PI * additionalWidthOverHalfCircle), 0 + (Math.PI * additionalWidthOverHalfCircle));
    ctx.stroke();
    let progress2 = percentage / 100;

    if (progress2 > 0.95 && progress2 !== 1) {
      progress2 *= 0.99;
    }

    if (progress2 > 0) {
      // Draw main arc with no rounded caps
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = thickness;
      ctx.lineCap = 'butt';
      const startAngle = Math.PI - (Math.PI * additionalWidthOverHalfCircle);
      const endAngle = startAngle + progress2 * (Math.PI + 2 * (Math.PI * additionalWidthOverHalfCircle));
      ctx.arc(center, center, radius, startAngle, endAngle);

      ctx.stroke();

      // Draw a rounded cap at the start of the arc
      const startX = center + radius * Math.cos(startAngle);
      const startY = center + radius * Math.sin(startAngle);
      ctx.beginPath();
      ctx.fillStyle = color;
      ctx.arc(startX, startY, thickness / 2, 0, 2 * Math.PI);
      ctx.fill();

      // Optionally, add a rounded cap at the end of the arc
      const endX = center + radius * Math.cos(endAngle);
      const endY = center + radius * Math.sin(endAngle);
      ctx.beginPath();
      ctx.fillStyle = color;
      ctx.arc(endX, endY, thickness / 2, 0, 2 * Math.PI);
      ctx.fill();
    }
  }, [maxValue, size, thickness, color, backgroundColor, percentage]);
  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block' }}
    />
  );
};

export default CanvasHalfCircle;
